'use client';

export default function CoreTeamSkeleton() {
  return null; // Main component doesn't need skeleton, individual sections have their own
}

// Individual skeleton components for different sections
CoreTeamSkeleton.Hero = function CoreTeamHeroSkeleton() {
  return (
    <section className="relative isolate min-h-screen overflow-hidden bg-gray-900">
      <div className="absolute inset-0 bg-gray-800 animate-pulse" />
      <div className="relative z-10 flex min-h-screen flex-col justify-end">
        <div className="container mx-auto px-4 py-12 sm:px-6 md:py-14 lg:py-16">
          <div className="max-w-[660px] space-y-4 animate-pulse">
            <div className="h-12 rounded-md bg-white/15 sm:h-14 md:h-16" />
            <div className="h-4 w-full rounded bg-white/10" />
            <div className="h-4 w-5/6 rounded bg-white/10" />
          </div>
        </div>
      </div>
    </section>
  );
};

CoreTeamSkeleton.Stats = function CoreTeamStatsSkeleton() {
  return (
    <section className="bg-[#f8f7f4] py-10 sm:py-12 md:py-14">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto max-w-3xl text-center animate-pulse">
            <div className="mx-auto h-10 max-w-2xl rounded-[10px] bg-gray-200 md:h-12" />
            <div className="mx-auto mt-3 h-4 max-w-xl rounded bg-gray-200" />
            <div className="mx-auto mt-2 h-4 max-w-lg rounded bg-gray-200" />
          </div>

          <div className="mx-auto mt-12 grid max-w-4xl gap-x-16 gap-y-14 md:mt-16 md:grid-cols-2">
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className={`animate-pulse ${index % 2 === 1 ? 'md:translate-y-12' : ''}`}
              >
                <div className="aspect-[1.22/0.86] w-full rounded-[14px] bg-gray-200" />
                <div className="pt-5">
                  <div className="h-8 max-w-[240px] rounded bg-gray-200" />
                  <div className="mt-2 h-4 max-w-24 rounded bg-gray-200" />
                  <div className="mt-4 space-y-2">
                    <div className="h-4 rounded bg-gray-200" />
                    <div className="h-4 rounded bg-gray-200" />
                    <div className="h-4 w-[82%] rounded bg-gray-200" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

CoreTeamSkeleton.Featured = function CoreTeamFeaturedSkeleton() {
  return (
    <section className="bg-white py-10 sm:py-12 md:py-14">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12 animate-pulse">
          <div className="mx-auto h-10 max-w-2xl rounded-[10px] bg-gray-200 md:h-12" />
          <div className="mx-auto mt-3 h-4 max-w-xl rounded bg-gray-200" />
          <div className="mx-auto mt-2 h-4 max-w-lg rounded bg-gray-200" />
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4 lg:gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="aspect-[0.88/0.9] w-full rounded-[10px] bg-gray-200" />
              <div className="pt-4">
                <div className="h-7 max-w-[180px] rounded bg-gray-200" />
                <div className="mt-2 h-4 max-w-28 rounded bg-gray-200" />
                <div className="mt-4 space-y-2">
                  <div className="h-4 rounded bg-gray-200" />
                  <div className="h-4 rounded bg-gray-200" />
                  <div className="h-4 w-[84%] rounded bg-gray-200" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

CoreTeamSkeleton.Message = function CoreTeamMessageSkeleton() {
  return (
    <section className="relative overflow-hidden bg-white py-10 sm:py-12 md:py-14">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-4xl">
          <div className="text-center animate-pulse">
            <div className="mx-auto h-10 max-w-md rounded-[10px] bg-gray-200 md:h-12" />
          </div>

          <div className="mx-auto mt-10 max-w-3xl overflow-hidden rounded-[12px] bg-gray-200 md:mt-12">
            <div className="aspect-[1.72/1] w-full animate-pulse bg-gray-200" />
          </div>

          <div className="mx-auto mt-8 max-w-3xl space-y-3 animate-pulse">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className={`h-4 rounded bg-gray-200 ${index % 3 === 0 ? 'w-[88%]' : 'w-full'}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

CoreTeamSkeleton.Filters = function CoreTeamFiltersSkeleton() {
  return (
    <div className="bg-white rounded-lg shadow-sm border p-6 animate-pulse">
      <div className="h-12 bg-gray-200 rounded-lg mb-6" />
      <div className="flex items-center justify-between mb-4">
        <div className="h-8 bg-gray-200 rounded max-w-24" />
        <div className="h-8 bg-gray-200 rounded max-w-20" />
      </div>
    </div>
  );
};

CoreTeamSkeleton.Departments = function CoreTeamDepartmentsSkeleton() {
  return (
    <div className="space-y-16">
      {Array.from({ length: 3 }).map((_, deptIndex) => (
        <div key={deptIndex} className="animate-pulse">
          <div className="text-center mb-12">
            <div className="h-8 bg-gray-200 rounded-lg mb-4 max-w-md mx-auto" />
            <div className="h-5 bg-gray-200 rounded-lg max-w-3xl mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, memberIndex) => (
              <div
                key={memberIndex}
                className="bg-white rounded-lg shadow-sm border overflow-hidden"
              >
                <div className="h-64 bg-gray-200" />
                <div className="p-6">
                  <div className="text-center mb-4">
                    <div className="h-5 bg-gray-200 rounded mb-2 max-w-24 mx-auto" />
                    <div className="h-4 bg-gray-200 rounded mb-1 max-w-32 mx-auto" />
                    <div className="h-3 bg-gray-200 rounded max-w-20 mx-auto" />
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded" />
                    <div className="h-3 bg-gray-200 rounded" />
                    <div className="h-3 bg-gray-200 rounded max-w-3/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

CoreTeamSkeleton.Sections = function CoreTeamSectionsSkeleton() {
  return (
    <div className="space-y-12 animate-pulse">
      {Array.from({ length: 2 }).map((_, index) => (
        <div key={index} className="max-w-4xl mx-auto">
          <div className="h-8 bg-gray-200 rounded-lg mb-4 max-w-md" />
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded max-w-5/6" />
          </div>
        </div>
      ))}
    </div>
  );
};
